import { toast } from "react-toastify";
import { deleteItems, updateStateData } from "../Store/actions";
import store from "../Store/store";
import CampaignIcon from "@mui/icons-material/Campaign";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { Announcement, DataArray, Insights, ManageAccounts, Settings } from "@mui/icons-material";
import { checkAuthorizedUserDetails, createCookie, deleteCookie, logout, sendApiRequest, setBatchLoadingStatus } from "./requestConfig";
import { validateEmailFormat } from "./utils";
import { loadRetailerCompanyIdsForAccountSave } from "./retailerSelection";
import { canEditAcrossCompanies } from "./crossCompanyAccess";

//to validate roles information
const validateRoleInformation = () => {
  const currentStateData = store.getState().userSelections;
  const roleName = typeof currentStateData.new_role_name !== "undefined" ? currentStateData.new_role_name : "";
  const rolePermissions = typeof currentStateData.role_permissions !== "undefined" ? currentStateData.role_permissions : {};

  let error = false;
  //validate role name
  if (roleName === "") {
    error = true;
  }

  //validate role permissions
  if (Object.keys(rolePermissions).length === 0) {
    error = true;
  }

  if (error === true) {
    toast("Please enter the role name and select the permission", { error: true });
    return {
      error: error,
    };
  } else {
    return {
      error: false,
      role_requestInfo: {
        role_name: roleName,
        permissions: rolePermissions,
      },
    };
  }
};

//to validate details when adding a new user
const mergeUniqueIds = (ids) => {
  return [...new Set(ids.filter((id) => typeof id !== "undefined" && id !== null && id !== ""))];
};

const validateUserInformation = (actionToPerform, refreshUserType = "retailer") => {
  const currentStateData = store.getState().userSelections;
  const normalizedRefreshUserType = normalizeUserListType(refreshUserType);
  const tenantUserMode = normalizedRefreshUserType === "tenant";
  const companySelectionMode =
    typeof currentStateData.company_selection_mode !== "undefined"
      ? currentStateData.company_selection_mode
      : tenantUserMode === true
        ? "multi"
        : "single";
  const hierarchyMultiMode = companySelectionMode === "hierarchy_multi";
  const hierarchySingleMode = companySelectionMode === "hierarchy_single";
  const userName = typeof currentStateData.username !== "undefined" ? currentStateData.username : "";
  const email = typeof currentStateData.email !== "undefined" ? currentStateData.email : "";
  const userRole = typeof currentStateData.roles !== "undefined" ? currentStateData.roles : [];
  const campaignIds =
    typeof currentStateData.campaign_id !== "undefined" && currentStateData.campaign_id !== null
      ? currentStateData.campaign_id
      : [];
  const cyreenUser = typeof currentStateData.cyreen_user !== "undefined" ? currentStateData.cyreen_user : false;
  // workspace_id workspace_name
  const userWorkspaceId =
    typeof currentStateData.selected_company_id !== "undefined" ? currentStateData.selected_company_id : "";
  const selectedTenantCompanyIds = Array.isArray(currentStateData.selected_company_ids)
    ? currentStateData.selected_company_ids
    : [];
  const hiddenTenantCompanyIds = Array.isArray(currentStateData.tenant_hidden_company_ids)
    ? currentStateData.tenant_hidden_company_ids
    : [];
  const multiWorkspaceIds = mergeUniqueIds([...hiddenTenantCompanyIds, ...selectedTenantCompanyIds]);
  // Users-tab edit keeps existing assignments here even when no new company is picked
  const retainedWorkspaceIds = Array.isArray(currentStateData.workspace_ids)
    ? currentStateData.workspace_ids
    : [];
  const isAddingToNewCompany = currentStateData.adding_to_new_company === true;
  const assignedCompanyDetails =
    typeof currentStateData.user_assigned_company !== "undefined" ? currentStateData.user_assigned_company : {};
  // const userWorkspaceName = typeof currentStateData.workspace_name !== "undefined" ? currentStateData.workspace_name : "";
  let error = false;
  let errorMessages = {};
  //validate user name
  if (userName === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      user_name: "Please enter the user name",
    };
  }

  //validate user name
  if (email === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      email: "Please enter the E-Mail",
    };
  } else {
    // validate for proper email format
    const validateEmail = validateEmailFormat(email);
    if (validateEmail === false) {
      error = true;
      errorMessages = {
        ...errorMessages,
        email: "Please enter a valid E-Mail",
      };
    }
  }
  //validate user name
  if (userRole.length === 0) {
    error = true;
    errorMessages = {
      ...errorMessages,
      user_role: "Please select at least one role",
    };
  }

  //validate role permissions
  // if (Object.keys(userPermissions).length === 0) {
  //   error = true;
  //   errorMessages = {
  //     ...errorMessages,
  //     user_permissions: "Please select the user permissions",
  //   };
  // }

  if (userRole === "Brand" && campaignIds.length === 0) {
    error = true;
    errorMessages = {
      ...errorMessages,
      campaign_id: "Please select the campaign",
    };
  }

  // Tenant retailers are chosen in the form. Cyreen employee accounts still receive every root in saveUserInformation.
  if (tenantUserMode === true && selectedTenantCompanyIds.length === 0) {
    error = true;
    errorMessages = {
      ...errorMessages,
      retailer: "Please select at least one retailer",
    };
  }

  if (tenantUserMode === false && hierarchyMultiMode === true && multiWorkspaceIds.length === 0) {
    error = true;
    errorMessages = {
      ...errorMessages,
      user_company: "Please select the Company the user belongs to",
    };
  }

  if (tenantUserMode === false && hierarchyMultiMode === false && hierarchySingleMode === true) {
    const hasNewCompany = typeof userWorkspaceId !== "undefined" && userWorkspaceId !== "";
    const existingCompanyIds = mergeUniqueIds([...retainedWorkspaceIds, ...multiWorkspaceIds]);
    // Add always needs a pick. Edit needs a pick only when "Add to new company" is selected.
    const missingNewCompany = isAddingToNewCompany === true && hasNewCompany !== true;
    const missingAddCompany = actionToPerform !== "update" && hasNewCompany !== true;
    const missingExistingOnUpdate =
      actionToPerform === "update" &&
      isAddingToNewCompany !== true &&
      hasNewCompany !== true &&
      existingCompanyIds.length === 0;

    if (missingNewCompany === true || missingAddCompany === true || missingExistingOnUpdate === true) {
      error = true;
      errorMessages = {
        ...errorMessages,
        user_company: "Please select the Company the user belongs to",
      };
    }
  }

  if (
    tenantUserMode === false &&
    hierarchyMultiMode === false &&
    hierarchySingleMode === false &&
    cyreenUser === false &&
    (typeof userWorkspaceId === "undefined" || userWorkspaceId === "")
  ) {
    error = true;
    errorMessages = {
      ...errorMessages,
      user_company: "Please select the Company the user belongs to",
    };
  }

  // cyreenUser
  // userWorkspaceId

  if (error === true) {
    toast("Please fill in valid information", { type: "error" });
    return {
      error: error,
      errorMessages,
    };
  } else {
    // userWorkspaceId
    let requestObj = {
      username: userName,
      email: email,
      roles: userRole,
      campaign_ids: campaignIds,
      cyreen_user: cyreenUser,
    };
    if (tenantUserMode === true) {
      requestObj = {
        ...requestObj,
        user_type: "tenant",
        // Roots chosen in Retailer* only. Ids the dropdown did not show are not sent back.
        workspace_ids: mergeUniqueIds(selectedTenantCompanyIds),
      };
    } else if (hierarchySingleMode === true) {
      // Keep assigned companies on update; add workspace_id only when a new company was picked
      requestObj = {
        ...requestObj,
        workspace_ids: mergeUniqueIds([...retainedWorkspaceIds, ...multiWorkspaceIds, userWorkspaceId]),
      };
      if (typeof userWorkspaceId !== "undefined" && userWorkspaceId !== "") {
        requestObj.workspace_id = userWorkspaceId;
      }
    } else if (hierarchyMultiMode === true) {
      // Older multi-company hierarchy payload (kept for any leftover callers)
      requestObj = {
        ...requestObj,
        workspace_ids: multiWorkspaceIds,
      };
    } else if (cyreenUser !== true && userWorkspaceId !== "") {
      requestObj = {
        ...requestObj,
        workspace_id: userWorkspaceId,
      };
    }
    // add assigned company obj if action = update (single-company path only)
    if (
      actionToPerform === "update" &&
      cyreenUser === false &&
      tenantUserMode === false &&
      hierarchyMultiMode === false &&
      hierarchySingleMode === false
    ) {
      requestObj = {
        ...requestObj,
        assigned_company: assignedCompanyDetails,
      };
    }
    return {
      error: false,
      user_requestInfo: requestObj,
    };
  }
};

export const manageUsersPaginationCookie = (paginationData) => {
  const currentStateData = store.getState().userSelections;
  //
  const paginationObj = typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination : {};

  store.dispatch(updateStateData({ users_pagination: { ...paginationObj, ...paginationData } }));

  deleteCookie("users_pagination");
  // set cookie for pagination
  createCookie("users_pagination", JSON.stringify({ ...paginationObj, ...paginationData }));
}

const USER_LIST_STATE_KEYS = {
  retailer: "users_list",
  agency: "agency_users_list",
  tenant: "tenant_users_list",
  brand: "brand_users_list",
  cyreen: "cyreen_users",
};

export const normalizeUserListType = (userType) => {
  const normalizedUserType = typeof userType !== "undefined" && userType !== null
    ? userType.toString().toLowerCase()
    : "retailer";

  if (["customer", "customers", "user", "users", "retailer"].includes(normalizedUserType)) {
    return "retailer";
  }

  if (["brand", "brands"].includes(normalizedUserType)) {
    return "brand";
  }

  if (["agency", "tenant", "cyreen"].includes(normalizedUserType)) {
    return normalizedUserType;
  }

  return "retailer";
};

export const userListStateKey = (userType) => {
  const normalizedUserType = normalizeUserListType(userType);
  return USER_LIST_STATE_KEYS[normalizedUserType];
};

export const userListFilterCookieKey = (userType) => {
  const normalizedUserType = normalizeUserListType(userType);

  if (normalizedUserType === "retailer") return "users_filter";
  return normalizedUserType + "_users_filter";
};

/**
 * Roles are returned per assigned company, so the same role can repeat across companies.
 * Flatten them into one A-Z list of unique names for the Role column, the search box
 * and the edit/delete checks that compare against the allowed role names.
 */
const uniqueRoleNamesFromCompanies = (assignedCompanies) => {
  const roleNames = assignedCompanies.flatMap((company) => {
    return Array.isArray(company.roles) ? company.roles : [];
  });

  return [...new Set(roleNames)].sort((next, prev) => next.localeCompare(prev));
};

/**
 * Each assigned company points at the retailer it belongs to (root_retailer_company).
 * Collect those roots once per id so the Retailer column lists every retailer the user
 * can reach, without repeating the same one for sibling companies.
 */
const uniqueRetailersFromCompanies = (assignedCompanies) => {
  const retailersById = new Map();

  assignedCompanies.forEach((company) => {
    const retailer = company.root_retailer_company;
    if (typeof retailer === "undefined" || retailer === null || typeof retailer.id === "undefined") {
      return;
    }
    if (retailersById.has(retailer.id) === false) {
      retailersById.set(retailer.id, { id: retailer.id, name: retailer.name });
    }
  });

  return [...retailersById.values()].sort((next, prev) => {
    return (next.name ?? "").localeCompare(prev.name ?? "");
  });
};

const normalizeUserRecord = (user) => {
  const assignedCompanies = Array.isArray(user.assigned_companies)
    ? user.assigned_companies
    : typeof user.assigned_company !== "undefined" && user.assigned_company !== null
      ? [user.assigned_company]
      : [];

  return {
    id: user.id,
    assigned_company: typeof user.assigned_company !== "undefined" && user.assigned_company !== null ? user.assigned_company : {},
    assigned_companies: assignedCompanies,
    // same flag brand users use: delete needs a company picker when the user is in more than one workspace
    multiple_accounts: assignedCompanies.length > 1,
    username: user.username,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    roles: uniqueRoleNamesFromCompanies(assignedCompanies),
    // retailer roots behind the assigned companies, used by the Retailer column and search
    retailers: uniqueRetailersFromCompanies(assignedCompanies),
    retailer_ids: user.retailer_ids,
    user_permissions: user.permissions,
    user_type: user.user_type,
    cyreen_user: user.cyreen_user,
    campaign_id: user.campaign_ids,
    workspace_id: user.workspace_id,
    workspace_ids: Array.isArray(user.workspace_ids)
      ? user.workspace_ids
      : assignedCompanies.map((company) => company.id).filter((id) => typeof id !== "undefined"),
    brand_campaign_selection:
      typeof user.campaigns !== "undefined" && user.campaigns !== null
        ? user.campaigns.map((campaign) => {
          return { id: campaign.id, label: campaign.name };
        })
        : [],
  };
};

const normalizeBrandUserList = (usersList) => {
  const userList = [];

  usersList.forEach((user) => {
    if (user.email === "" || user.email === null) return;

    const assignedCompanies = Array.isArray(user.assigned_companies) ? user.assigned_companies : [];
    // Campaign details are nested under each assigned company in the user-list response.
    // Keep the full campaign objects and add the ID list expected by the edit form.
    const assignedCompaniesWithCampaignIds = assignedCompanies.map((company) => {
      const campaigns = Array.isArray(company.campaigns) ? company.campaigns : [];
      const campaignIds = campaigns
        .map((campaign) => campaign.id)
        .filter((campaignId) => typeof campaignId !== "undefined");

      return {
        ...company,
        campaign_ids: campaignIds,
      };
    });

    userList.push({
      ...user,
      // roles and retailer roots now arrive per company — flatten them like the other lists
      roles: uniqueRoleNamesFromCompanies(assignedCompaniesWithCampaignIds),
      retailers: uniqueRetailersFromCompanies(assignedCompaniesWithCampaignIds),
      assigned_companies: assignedCompaniesWithCampaignIds,
      multiple_accounts: assignedCompanies.length > 1,
    });
  });

  return userList;
};

// to fetch a list of users
export const fetchUsers = async (userType = "retailer", campaignId) => {
  const normalizedUserType = normalizeUserListType(userType);
  let usersEndpoint = "users?type=" + normalizedUserType;
  if (normalizedUserType === "brand" && typeof campaignId !== "undefined" && campaignId !== "") {
    usersEndpoint += "&campaign_id=" + encodeURIComponent(campaignId);
  }
  const getUsersList = await sendApiRequest(usersEndpoint, {}, "GET");

  if (typeof getUsersList.success !== "undefined" && getUsersList.success === true) {
    const usersList = Array.isArray(getUsersList.response) ? getUsersList.response : [];

    let filteredList = [];
    let userIdandNameList = {};

    if (normalizedUserType === "brand") {
      filteredList = normalizeBrandUserList(usersList);
    } else {
      usersList.forEach((user) => {
        if (user.email === "" || user.email === null) return;
        filteredList.push(normalizeUserRecord(user));
        userIdandNameList =
          user.role_name === "Retailer"
            ? {
              ...userIdandNameList,
              [user.id]: user.username,
            }
            : { ...userIdandNameList };
      });
    }

    let stateUpdate = {
      [userListStateKey(normalizedUserType)]: filteredList,
      user_name_id_list: userIdandNameList,
    };

    if (normalizedUserType === "brand") {
      stateUpdate = {
        ...stateUpdate,
        brand_user_list_options: filteredList.map((user) => {
          return canAssignOrMarkFavorite(user) ? { label: user.email, value: user.id, fav: user.favourited } : null;
        }).filter(item => item !== null),
      };
    }

    store.dispatch(updateStateData(stateUpdate));

    manageUsersPaginationCookie({
      user_list_start: 0,
      user_list_end: 10,
    });

    // scroll to the top of page
    window.scrollTo(0, 0);

    return {
      success: true,
    };
  } else {
    return {
      success: false,
    };
  }
};

// to fetch a list of users that can be assigned to a specific role.
export const fetchAssignableUsers = async (roleId) => {
  const getUsersList = await sendApiRequest("users/assignable/" + roleId, {}, "GET");
  if (typeof getUsersList.success !== "undefined" && getUsersList.success === true) {
    const usersList = getUsersList.response;

    let filteredLst = [];
    let filteredCyreenUsers = [];
    usersList.forEach((user) => {
      if (user.email !== "" && user.email !== null) {
        let restructuredUserObj = {
          id: user.id,
          assigned_company: user.assigned_company,
          username: user.username,
          email: user.email,
          roles: user.roles.sort((next, prev) => {
            return next.localeCompare(prev);
          }),
        };

        if (user.user_type === "cyreen") {
          filteredCyreenUsers.push(restructuredUserObj);
        } else {
          filteredLst.push(restructuredUserObj);
        }
      }
    });

    store.dispatch(
      updateStateData({
        assignable_users_list: filteredLst,
        assignable_cyreen_users: filteredCyreenUsers,
      })
    );
    return {
      success: true,
    };
  } else {
    return {
      success: false,
    };
  }
};

export const fetchRoleDetail = async (role) => {
  let detailObj = {};
  // request additional info for each role
  const getRolesDetail = await sendApiRequest("users/roles/" + role.id, {}, "GET");
  if (typeof getRolesDetail.success !== "undefined" && getRolesDetail.success === true) {
    detailObj = {
      ...role,
      assigned_users: getRolesDetail.response.assigned_users,
      assigned_users_count: getRolesDetail.response.assigned_users_count,
      manage: getRolesDetail.response.manage,
      view: getRolesDetail.response.view,
    };

    return detailObj;
  } else {
    return {};
  }
};

const availableRoleList = (rolesList, fetchRelatedInfo) => {
  return fetchRelatedInfo === false
    ? rolesList.filter(role => typeof role.role_name !== "undefined" && role.role_name.toLowerCase() !== "brand")
    : rolesList;
};

const updateRolesState = (rolesList, fetchRelatedInfo) => {
  const rolesForState = availableRoleList(rolesList, fetchRelatedInfo);

  store.dispatch(
    updateStateData({
      role_list: rolesForState,
      role_name_arr: rolesList.map((role) => role.role_name).filter((roleName) => typeof roleName !== "undefined"),
    })
  );

  return rolesForState;
};

// type is the user being added (retailer, tenant, brand, cyreen), not a company level.
// companyId is sent only when the roles must be the ones allowed for that company.
const rolesListEndpoint = (roleQuery = {}) => {
  const queryParams = new URLSearchParams();
  const roleType = roleQuery.type;
  const companyId = roleQuery.companyId;

  if (typeof roleType === "string" && roleType !== "") {
    queryParams.set("type", roleType);
  }
  if (typeof companyId !== "undefined" && companyId !== null && companyId !== "") {
    queryParams.set("companyId", String(companyId));
  }

  const query = queryParams.toString();
  return query === "" ? "users/roles/list" : "users/roles/list?" + query;
};

// fetch list of available roles
export const fetchRoles = async (fetchRelatedInfo, roleQuery = {}) => {
  // reset roles data in redux before fetching new data
  store.dispatch(deleteItems(["role_list", "role_name_arr"]));
  // request end point
  const endpoint = rolesListEndpoint(roleQuery);
  // fetch roles list
  const getRolesList = await sendApiRequest(endpoint, {}, "GET");
  if (typeof getRolesList.success !== "undefined" && getRolesList.success === true) {
    let rolesList = Array.isArray(getRolesList.response) ? getRolesList.response : [];
    // Fetch details for each role and update the list
    if (fetchRelatedInfo === true) {
      const rolesWithDetails = await Promise.all(
        rolesList.map(async (role) => {
          const roleDetail = await fetchRoleDetail(role);
          return { ...role, ...roleDetail };
        })
      );
      const rolesForState = updateRolesState(rolesWithDetails, fetchRelatedInfo);
      return {
        success: true,
        roles_list: rolesForState,
      };
    } else {
      const rolesForState = updateRolesState(rolesList, fetchRelatedInfo);
      return {
        success: true,
        roles_list: rolesForState,
      };
    }
  } else {
    return {
      success: false,
    };
  }
};

export const fetchRolesForCompanies = async (fetchRelatedInfo, roleType, companyIds = []) => {
  const uniqueCompanyIds = mergeUniqueIds(companyIds);
  const companyId = uniqueCompanyIds.length > 0 ? uniqueCompanyIds[0] : "";

  return fetchRoles(fetchRelatedInfo, {
    type: roleType,
    companyId,
  });
};

export const assignRoleToUsers = async (userInfo, roleId, refreshUserType = "retailer") => {
  const assignRole = await sendApiRequest("users/roles/" + roleId + "/assign", userInfo, "POST");
  if (typeof assignRole.success !== "undefined" && assignRole.success === true) {
    if (assignRole.response.success === true) {
      fetchUsers(refreshUserType);
      return {
        success: true,
      };
    } else {
      toast("Sorry, something went wrong", { type: "error" });
    }
  } else {
    toast("Sorry, something went wrong", { type: "error" });
    return {
      success: false,
    };
  }
};

export const deleteUser = async (userToDelete, refreshUserType = "retailer") => {
  const workspaceId = userToDelete?.assigned_company?.id ?? userToDelete?.assigned_companies?.[0]?.id;
  const deleteRecord = await sendApiRequest(
    "users/delete",
    {
      user_id: userToDelete.id,
      email: userToDelete.email,
      workspace_id: workspaceId,
      // The tab decides the access being removed. A tenant who also has brand
      // access is user_type tenant, but a Brand-tab delete must send brand.
      access_level: normalizeUserListType(refreshUserType),
    },
    "POST"
  );
  if (typeof deleteRecord.success !== "undefined" && deleteRecord.success === true) {
    if (deleteRecord.response.success === true) {
      fetchUsers(refreshUserType);
      return {
        success: true,
      };
    } else {
      toast(deleteRecord.response.error_detail, { type: "error" });
    }
  } else {
    toast("Sorry, something went wrong", { type: "error" });
    return {
      success: false,
    };
  }
};

// to delete user from a specific company (usecase: when a brand user is associated with multiple companies)
export const deleteUserFromSpecificCompany = async (userToDelete, company_id, refreshUserType = "brand") => {
  const deleteRecord = await sendApiRequest(
    "users/delete",
    {
      user_id: userToDelete.id,
      email: userToDelete.email,
      workspace_id: company_id,
      // Same tab rule as deleteUser. Multi-company brand delete stays brand.
      access_level: normalizeUserListType(refreshUserType),
    },
    "POST"
  );
  if (typeof deleteRecord.success !== "undefined" && deleteRecord.success === true) {
    if (deleteRecord.response.success === true) {
      fetchUsers(refreshUserType);
      return {
        success: true,
      };
    } else {
      toast(deleteRecord.response.error_detail, { type: "error" });
    }
  } else {
    toast("Sorry, something went wrong", { type: "error" });
    return {
      success: false,
    };
  }
};

// to fetch the list of companies
export const fetchCompanies = async () => {
  const getCompaniesList = await sendApiRequest("companies", {}, "GET", { skipRetailerViewFilter: true });
  if (typeof getCompaniesList.success !== "undefined" && getCompaniesList.success === true) {
    fetchBrands();
    const companiesList = getCompaniesList.response;
    let filtredRetailerList = [];
    let companiesNameIds = [];
    companiesList.forEach((company) => {
      companiesNameIds.push({ label: company.name, id: company.id });
      filtredRetailerList.push({ label: company.name, id: company.id, tree_path: company.tree_path });
    });

    store.dispatch(
      updateStateData({
        company_name_ids: companiesNameIds,
        retailers_list: filtredRetailerList,
      })
    );
    return {
      success: true,
    };
  } else {
    return {
      success: false,
    };
  }
};

// to fetch the list of companies
export const fetchStoreLevelCompanies = async (requestedList, ownerRequestedFor) => {
  setBatchLoadingStatus(true)
  let endpoint = "companies/share-list?type=" + requestedList;
  if (typeof ownerRequestedFor !== "undefined" && ownerRequestedFor !== "") {
    endpoint += "&owner_id=" + ownerRequestedFor;
  }
  const getCompaniesList = await sendApiRequest(endpoint, {}, "GET", { skipRetailerViewFilter: true });
  if (typeof getCompaniesList.success !== "undefined" && getCompaniesList.success === true) {
    // fetch advertisers/brand
    if (typeof ownerRequestedFor !== "undefined" && ownerRequestedFor !== "") {
      fetchBrands(ownerRequestedFor);
    } else {
      fetchBrands();
    }

    const companiesList = getCompaniesList.response;
    let filtredRetailerList = [];
    let companiesNameIds = [];
    companiesList.forEach((company) => {
      companiesNameIds.push({ label: company.name, id: company.id });
      filtredRetailerList.push({ label: company.name, id: company.id, tree_path: company.tree_path });
    });

    let finalDataForRedux = {
      store_level_company_name_ids: companiesNameIds,
      store_level_retailers_list: filtredRetailerList,
    };

    store.dispatch(
      updateStateData(finalDataForRedux)
    );
    setBatchLoadingStatus(false)
    return {
      success: true,
      ...finalDataForRedux,
    };
  } else {
    setBatchLoadingStatus(false)
    return {
      success: false,
    };
  }
};

export const fetchBrands = async (ownerRequestedFor) => {
  let endpoint = "companies/advertisers";

  if (typeof ownerRequestedFor !== "undefined" && ownerRequestedFor !== "") {
    endpoint += "?id_company_owner=" + ownerRequestedFor;
  }
  const getCompaniesList = await sendApiRequest(endpoint, {}, "GET", { skipRetailerViewFilter: true });
  if (typeof getCompaniesList.success !== "undefined" && getCompaniesList.success === true) {
    const companiesList = getCompaniesList.response;
    let filteredBrandList = [];
    let filteredBrandNames = [];

    companiesList.forEach((company) => {
      filteredBrandList.push({ label: company.name, id: company.id });
      filteredBrandNames.push(company.name);
    });
    // filteredBrandList.push({ label: "Add new", id: "new" });
    store.dispatch(
      updateStateData({
        brand_list: [{ label: "Add new", id: "new" }, ...filteredBrandList],
        brand_names_arr: filteredBrandNames,
      })
    );
    return {
      success: true,
    };
  } else {
    return {
      success: false,
    };
  }
};

// to save new user details
export const saveUserInformation = async (actionToPerform, actionDetail, refreshUserType = "retailer") => {
  const validateUser = validateUserInformation(actionToPerform, refreshUserType);
  // check for errors
  if (validateUser.error === false) {
    let requestInfo = validateUser.user_requestInfo;
    // Tenant saves already carry the Retailer* selection. Only Cyreen employee accounts still get every root.
    const attachAllRetailerWorkspaces = requestInfo.cyreen_user === true;

    if (attachAllRetailerWorkspaces === true) {
      const workspaceIds = await loadRetailerCompanyIdsForAccountSave();
      if (workspaceIds.length === 0) {
        toast("Sorry, something went wrong.", { type: "error" });
        return {
          success: false,
          errorMessages: {},
        };
      }
      requestInfo = {
        ...requestInfo,
        workspace_ids: workspaceIds,
      };
    }

    let apiSuccessResponse = "";
    if (actionToPerform === "add") {
      const saveUserInfo = await sendApiRequest("users/add", requestInfo, "POST");
      apiSuccessResponse = saveUserInfo.response;
    }

    if (actionToPerform === "update") {
      let requestBody = {
        ...requestInfo,
        id: actionDetail.id,
      };
      const saveUserInfo = await sendApiRequest("users/update", requestBody, "POST");
      apiSuccessResponse = saveUserInfo.response;
    }

    if (apiSuccessResponse.success === true) {
      fetchUsers(refreshUserType);
      return {
        success: true,
      };
    } else {
      const toastMessage =
        typeof apiSuccessResponse.error_detail !== "undefined" && apiSuccessResponse.error_detail !== ""
          ? apiSuccessResponse.error_detail
          : "Sorry, something went wrong.";
      toast(toastMessage, { type: "error" });
      return {
        success: false,
        errorMessages: {},
      };
    }
  } else {
    return {
      success: false,
      errorMessages: validateUser.errorMessages,
    };
  }
};

export const checkUserRole = () => {
  //check if user permission cookie is available or not.
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("userRole")) !== "undefined") {
    const role = JSON.parse(
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("userRole"))
        .split("=")[1]
    );

    return {
      success: true,
      user_role: role,
    };
  } else {
    // if cookie doesn't exist, then we have to ask user to login again so that correct user permissions can be fetched and used for all the features to work correctly
    logout();
  }
};

// fetch user detail from the cookie
// logoutIfMissing = false is for callers that also run while nobody is logged in yet (e.g. the announcement inbox,
// which is mounted on the login page and between setting the authToken and the userDetail cookie during login)
export const checkUserDetailsFromCookie = (logoutIfMissing = true) => {
  //check if user permission cookie is available or not.
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("userDetail")) !== "undefined") {
    const details = JSON.parse(
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("userDetail"))
        .split("=")[1]
    );

    return {
      success: true,
      user_detail: details,
    };
  } else if (logoutIfMissing === false) {
    return {
      success: false,
    };
  } else {
    // if cookie doesn't exist, then we have to ask user to login again so that correct user permissions can be fetched and used for all the features to work correctly
    logout();
  }
};

// Owner information belongs to companies above the logged in user's own company,
// so users assigned to a level 3 or 4 company must never see it.
// Brand users must never see it either, even if their assigned company is not level 3 or 4.
// Single source of truth for every store Owner column, filter and export.
export const canViewStoreOwner = () => {
  const checkUserDetails = checkUserDetailsFromCookie();
  const userDetail = checkUserDetails?.user_detail;
  const userType = typeof userDetail?.user_type === "string" ? userDetail.user_type.toLowerCase() : "";
  const companyLevel = userDetail?.assigned_company?.level;

  if (userType === "brand") {
    return false;
  }

  if (typeof companyLevel === "undefined") {
    return false;
  }

  return ![3, 4].includes(parseInt(companyLevel));
};

export const checkUserPermissions = () => {
  //check if user permission cookie is available or not.
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("userPermissions")) !== "undefined") {
    const permissionScope = JSON.parse(
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("userPermissions"))
        .split("=")[1]
    );
    const userRole = checkUserRole();

    const userDetails = checkUserDetailsFromCookie();

    return {
      success: true,
      allowed_access: permissionScope,
      user_role: userRole.user_role,
      user_type: userDetails.success === true ? userDetails.user_detail.user_type : "",
    };
  } else {
    // if cookie doesn't exist, then we have to ask user to login again so that correct user permissions can be fetched and used for all the features to work correctly
    logout();
  }
};

export const allowedNavOptions = async (location, accessFor) => {

  //check user permissions.
  const permissions = checkUserPermissions();
  const checkUserDetails = checkUserDetailsFromCookie();

  if (permissions.success === true) {
    const permissionScope = permissions.allowed_access;
    const userRole = permissions.user_role;
    const userType = checkUserDetails.success === true ? checkUserDetails.user_detail.user_type : "";

    // Define an array of navigation items with their paths
    let navItems = [];
    let deactivatedSections = checkUserDetails.success === true ? checkUserDetails.user_detail.deactivated_sections : [];
    // ["overview", "campaigns_section", "campaign_list", "clips", "pictures", "retail_analytics_section", "general_analytics", "instore_duration", "profitability", "trolley_usage", "heatmap", "optimization", "infrastructure_section", "stores_infra", "monitoring_infra", "data_exchange_section", "data_exchange_upload", "data_exchange_download", "user_section", "user_list", "role_list"];
    // overview
    if ((userType === "cyreen" || !deactivatedSections.includes("overview")) && permissionScope.overview_can_view === true) {
      navItems = [
        ...navItems,
        {
          path: "/cap-overview",
          text: "Overview",
          icon: <DashboardIcon />,
          nested_list: false,
          active: ["/cap-overview"].includes(location.pathname),
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        },
      ];
    }

    // ferreroDashboard
    if ((!deactivatedSections.includes("customDashboard")) && permissionScope.overview_can_view === true) {
      let nestedCustomDashList = [
        {
          path: process.env.REACT_APP_ENV_NAME === "demo" ? "/standard-dashboard" : "/ferrero-dashboard",
          text: "Standard Dashboard",
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        }, {
          path: process.env.REACT_APP_ENV_NAME === "demo" ? "/tailored-dashboard" : "/tailored-ferrero-dashboard",
          text: "Tailored Dashboard",
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        }
      ];

      navItems = [
        ...navItems,
        {
          path: "/ferrero-dashboard",
          text: "Dashboard",
          icon: <DashboardIcon />,
          nested_list: true,
          nested_keyword: "custom_dashboard_sub_menu",
          active: [
            "/ferrero-dashboard",
            "/tailored-ferrero-dashboard",
            "/standard-dashboard",
            "/tailored-dashboard"
          ].includes(location.pathname),
          nested_items: nestedCustomDashList,
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        },
      ];
    }

    // campaigns
    if (
      (userType === "cyreen" || !deactivatedSections.includes("campaigns_section")) &&
      (permissionScope.campaign_list_can_view === true ||
        permissionScope.clip_list_can_view === true ||
        permissionScope.picture_list_can_view === true ||
        permissionScope.listing_check_can_edit === true)
    ) {
      let roleBasedAccessItems = [];

      if ((userType === "cyreen" || !deactivatedSections.includes("campaign_list")) && permissionScope.campaign_list_can_view === true) {
        roleBasedAccessItems = [
          ...roleBasedAccessItems,
          {
            path: "/check-campaigns/list",
            text: "Contact-based Campaigns",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }

      if ((userType === "cyreen" || !deactivatedSections.includes("clips")) && permissionScope.clip_list_can_view === true) {
        roleBasedAccessItems = [
          ...roleBasedAccessItems,
          {
            path: "/cb-visuals",
            text: "Contact-based Visuals",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }
      if ((userType === "cyreen" || !deactivatedSections.includes("pictures")) && permissionScope.picture_list_can_view === true) {
        roleBasedAccessItems = [
          ...roleBasedAccessItems,
          {
            path: "/check-campaigns/loop-based-campaigns",
            text: "Loop-based Campaigns",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }

      if ((userType === "cyreen" || !deactivatedSections.includes("listingCheck")) && permissionScope.listing_check_can_edit === true) {
        roleBasedAccessItems = [
          ...roleBasedAccessItems,
          {
            path: "/listing-check",
            text: "Listing Check",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }

      if ((userType === "cyreen" || !deactivatedSections.includes("capacityCheck")) && (userType === "cyreen" && permissionScope.advertising_capacity_can_view === true)) {
        roleBasedAccessItems = [
          ...roleBasedAccessItems,
          {
            path: "/advertising-capacity",
            text: "Capacity Planning",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }

      navItems = [
        ...navItems,
        {
          path: "/check-campaigns/list",
          text: "Campaigns",
          icon: <CampaignIcon />,
          nested_list: true,
          nested_keyword: "campaign_sub_menu",
          active: [
            "/check-campaigns/list",
            "/cb-visuals",
            "/check-campaigns/loop-based-campaigns",
            "/check-campaigns/Companies",
            "/check-campaigns/Contacts",
            "/check-campaigns/dashboard",
            "/check-campaigns/campaign-analytics",
            "/listing-check",
            "/advertising-capacity",
            "/check-campaigns/data-sanity",
          ].some(path => location.pathname.startsWith(path)),
          nested_items: roleBasedAccessItems,
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        },
      ];
    }
    // retail analytics
    if ((userType === "cyreen" || !deactivatedSections.includes("retail_analytics_section")) && permissionScope.analytics_can_view === true && userType !== "national") {
      // temporary addition of a dashboard specially for edeka..
      let nestedItemsList = []
      // general analytics
      if (userType === "cyreen" || !deactivatedSections.includes("general_analytics")) {
        nestedItemsList = [
          ...nestedItemsList,
          {
            path: "/analytics/general-kpi",
            text: "General KPIs",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }
      // instore duration
      if (userType === "cyreen" || !deactivatedSections.includes("instore_duration")) {
        nestedItemsList = [
          ...nestedItemsList,
          {
            path: "/analytics/duration",
            text: "Instore Duration",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }
      // profitability
      if (userType === "cyreen" || !deactivatedSections.includes("profitability")) {
        nestedItemsList = [
          ...nestedItemsList,
          {
            path: "/analytics/profitability",
            text: "Profitability",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }
      // trolley usage
      if (userType === "cyreen" || !deactivatedSections.includes("trolley_usage")) {
        nestedItemsList = [
          ...nestedItemsList,
          {
            path: "/analytics/trolley-usage",
            text: "Trolley Usage",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }
      // heatmap
      if (userType === "cyreen" || !deactivatedSections.includes("heatmap")) {
        nestedItemsList = [
          ...nestedItemsList,
          {
            path: "/analytics/heat-map",
            text: "Heatmap",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }
      // optimization
      if (userType === "cyreen" || !deactivatedSections.includes("optimization")) {
        nestedItemsList = [
          ...nestedItemsList,
          {
            path: "/analytics/optimization",
            text: "Optimization",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }

      // root company id for edeka is 74, so we are hardcoding it to specifically show it to edeka.
      // if (checkUserDetails.success === true && checkUserDetails.user_detail.root_company_id === 74) {
      //   nestedItemsList = [
      //     ...nestedItemsList,
      //     {
      //       path: "/analytics/special-analytics",
      //       text: "Special Analytics",
      //     },
      //   ];
      // }
      navItems = [
        ...navItems,
        {
          path: "/analytics/general-kpi",
          text: "Retail Analytics",
          icon: <Insights />,
          nested_list: true,
          nested_keyword: "retail_analytics",
          active: [
            "/analytics/general-kpi",
            "/analytics/profitability",
            "/analytics/trolley-usage",
            "/analytics/duration",
            "/analytics/heat-map",
            "/analytics/optimization",
            "/analytics/special-analytics",
          ].some(path => location.pathname.startsWith(path)),
          nested_items: nestedItemsList,
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        },
      ];
    }
    // infrastructure
    if ((userType === "cyreen" || !deactivatedSections.includes("infrastructure_section")) &&
      (permissionScope.infra_store_list_can_view === true ||
        (permissionScope.infra_store_monitoring_can_view === true && userType !== "national"))
    ) {
      let nestedInfraList = [];
      if ((userType === "cyreen" || !deactivatedSections.includes("stores_infra")) && (permissionScope.infra_store_list_can_view === true || permissionScope.infra_store_detail_can_view === true)) {
        nestedInfraList = [
          ...nestedInfraList,
          {
            path: "/infrastructure/locations",
            text: "Stores",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }

      if ((userType === "cyreen" || !deactivatedSections.includes("monitoring_infra")) && permissionScope.infra_store_monitoring_can_view === true) {
        nestedInfraList = [
          ...nestedInfraList,
          {
            path: "/infrastructure/monitoring",
            text: "Monitoring",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }
      navItems = [
        ...navItems,
        {
          path: "/infrastructure/locations",
          text: "Infrastructure",
          icon: <Settings />,
          nested_list: true,
          nested_keyword: "infrastructure_sub_menu",
          active: [
            "/infrastructure/locations",
            "/infrastructure/devices",
            "/infrastructure/monitoring",
            "/infrastructure/configure-location",
          ].some(path => location.pathname.startsWith(path)),
          nested_items: nestedInfraList,
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        },
      ];
    }
    // Data Exchange
    if (
      (userType === "cyreen" || !deactivatedSections.includes("data_exchange_section")) &&
      (permissionScope.data_exchange_upload_can_view === true ||
        permissionScope.data_exchange_download_can_view === true)
    ) {
      navItems = [
        ...navItems,
        {
          path: "/data-exchange",
          text: "Data Exchange",
          icon: <DataArray />,
          nested_list: false,
          nested_keyword: "data_exchange_menu",
          active: ["/data-exchange"].some(path => location.pathname.startsWith(path)),
          nested_items: [
            {
              path: "/data-exchange",
              text: "Data Exchange",
            },
          ],
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        },
      ];
    }

    // users list
    if ((userType === "cyreen" || !deactivatedSections.includes("user_section")) && permissionScope.user_list_can_view === true) {
      let nestedUserSections = [];
      if (userType === "cyreen" || !deactivatedSections.includes("user_list")) {
        nestedUserSections = [
          ...nestedUserSections,
          {
            path: "/users/list",
            text: "User List",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }
      if (userType === "cyreen" || !deactivatedSections.includes("role_list")) {
        nestedUserSections = [
          ...nestedUserSections,
          {
            path: "/users/role",
            text: "Roles",
            disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
          },
        ];
      }
      navItems = [
        ...navItems,
        {
          path: "/users/list",
          text: "Users",
          icon: <ManageAccounts />,
          nested_list: true,
          nested_keyword: "user_management_menu",
          active: ["/users/list", "/users/role"].some(path => location.pathname.startsWith(path)),
          nested_items: nestedUserSections,
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        },
      ];
    }

    // announcements
    if (
      userType === "cyreen" &&
      (permissionScope.announcement_can_view === true || permissionScope.announcement_can_edit === true)
    ) {
      navItems = [
        ...navItems,
        {
          path: "/announcements",
          text: "Announcements",
          icon: <Announcement />,
          nested_list: false,
          nested_keyword: "announcements_menu",
          active: ["/announcements"].some(path => location.pathname.startsWith(path)),
          nested_items: [
            {
              path: "/announcements",
              text: "Announcements",
              disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
            },
          ],
          disabled: typeof accessFor !== "undefined" && accessFor === "preview" ? true : false
        },
      ];
    }

    return {
      success: true,
      navItems: navItems,
    };
  } else {
    // if cookie doesn't exist, then we have to ask user to login again so that correct user permissions can be fetched and used for all the features to work correctly
  }
};

export const pageAccessAllowed = (routeToAccess, allowedUserTypes = []) => {
  // return {
  //   success: true,
  //   message: "Access allowed",
  // };
  // Check allowed access
  const permissions = checkUserPermissions();

  if (permissions.success === true) {
    const permissionScope = permissions.allowed_access;
    const userType = permissions.user_type;

    // Check if user type is allowed
    if (allowedUserTypes.length > 0 && !allowedUserTypes.includes(userType)) {
      return {
        success: false,
        message: "Sorry, your user type is not allowed to access this page",
      };
    }

    // loop through requested access rights and return the response accordingly
    let allowed = false;
    routeToAccess.forEach((access_type) => {
      if (permissionScope[access_type] === true) {
        allowed = true;
      }
    });

    if (allowed === true) {
      return {
        success: true,
        message: "Access allowed",
      };
    } else {
      return {
        success: false,
        message: "Sorry, you are not allowed to access this page",
      };
    }
  } else {
    return {
      success: false,
      message: "Sorry, something went wrong",
    };
  }
};

// to reset password for the logged user
export const resetUserPassword = async (passwordInfo) => {
  //update password
  const updateInfo = await sendApiRequest("users/update-password", { password: passwordInfo }, "POST");
  if (typeof updateInfo.success !== "undefined" && updateInfo.success === true) {
    logout();
  } else {
    toast("Sorry, something went wrong.", { type: "error" });
  }
};

// to send forgot password email
export const forgotUserPassword = async (userEmail, setLastResetedAt) => {
  //catch empty case to not trigger cooldown or API req
  if (userEmail === "") {
    toast("Please enter email", { type: "error" })
    return
  }
  //update password
  const sendInfo = await sendApiRequest("authenticate/password-reset", { email: userEmail }, "POST");

  if (typeof sendInfo.success !== "undefined" && sendInfo.success === true) {
    toast("Password reset successful", { type: "success" });
    setLastResetedAt(Date.now())
  } else {
    toast("Sorry, something went wrong.", { type: "error" });
  }
};

// switch workspace
export const switchWorkspace = async (workspaceId, navigate, usedFor) => {
  //update password
  const sendInfo = await sendApiRequest("workspaces/switch/" + workspaceId, {}, "POST");

  if (typeof sendInfo.success !== "undefined" && sendInfo.success === true) {
    if (usedFor === "update") {
      toast(sendInfo.response.message, { type: "success" });
    }

    store.dispatch(
      updateStateData({
        show_workspace_list: false,
      })
    );
    checkAuthorizedUserDetails(navigate, usedFor);
  } else {
    toast("Sorry, something went wrong.", { type: "error" });
  }
};

export const switchToBrandAccess = async (navigate, usedFor) => {
  checkAuthorizedUserDetails(navigate, usedFor, "brand");
};

export const checkWorkSpaceFromCookies = () => {
  let currentWorkSpace = {};
  let workspaceOptions = [];
  // check if workspace options or tenant options is available
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("workspace_options")) !== "undefined" || typeof document.cookie.split("; ").find((row) => row.startsWith("current_workspace")) !== "undefined") {
    // workspace options
    if (typeof document.cookie.split("; ").find((row) => row.startsWith("workspace_options")) !== "undefined") {
      workspaceOptions = JSON.parse(
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("workspace_options"))
          .split("=")[1]
      );
    }

    if (typeof document.cookie.split("; ").find((row) => row.startsWith("current_workspace")) !== "undefined") {
      currentWorkSpace = JSON.parse(
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("current_workspace"))
          .split("=")[1]
      );
    }

    return {
      success: true,
      workspace_options: workspaceOptions,
      current_workspace: currentWorkSpace,
    };
  } else {
    return {
      success: false,
      workspace_options: [],
      current_workspace: {},
    };
  }
};

export const fetchBrandUsers = async (campaignId) => {
  return fetchUsers("brand", campaignId);
};

export const markFavoriteUser = async (userEmail, refreshUserType = "brand") => {
  const markFavUser = await sendApiRequest("users/favourite", { email: userEmail }, "POST");
  if (typeof markFavUser.success !== "undefined" && markFavUser.success === true) {
    fetchUsers(refreshUserType);
    return {
      success: true,
    };
  } else {
    toast("Sorry, something went wrong", { type: "error" });
    return {
      success: false,
    };
  }
};

// manage pagination cookie infomation
export const manageShareableCampaignPaginationCookie = (campaignPaginationObj) => {
  const currentStateData = store.getState().userSelections;
  //
  const paginationObj = typeof currentStateData.shareable_campaign_pagination !== "undefined" ? currentStateData.shareable_campaign_pagination : {};

  store.dispatch(updateStateData({ shareable_campaign_pagination: { ...paginationObj, ...campaignPaginationObj } }));
  // deleteCookie("campaign_pagination");
  // // set cookie for pagination
  // createCookie("campaign_pagination", JSON.stringify({ ...paginationObj, ...campaignPaginationObj }));

}

export const fetchShareableCampaigns = async (ownerId, pageNumber, pageSize, search_term) => {
  const currentStateData = store.getState().userSelections;
  // check if page number and page size are passed from the component, if not then set default values.
  if (typeof pageNumber === "undefined" && typeof pageSize === "undefined") {
    pageNumber = 0;
    pageSize = 10;
    // page number/size from the cookies if available
    if (typeof currentStateData.shareable_campaign_pagination !== "undefined") {
      pageNumber = currentStateData.shareable_campaign_pagination.pageNumber;
      pageSize = currentStateData.shareable_campaign_pagination.pageSize;
    }
  }

  let campaignListApiEndpoint = "users/campaigns?page=" + pageNumber + "&size=" + pageSize;

  // append owner id if not empty
  if (typeof ownerId !== "undefined" && ownerId !== "") {
    campaignListApiEndpoint = campaignListApiEndpoint + "&id_company=" + ownerId;
  }

  // apply sorting if available
  if (typeof currentStateData.brand_campaign_sorting_measure !== "undefined" && currentStateData.brand_campaign_sorting_measure.sortField !== "" && currentStateData.brand_campaign_sorting_measure.sortOrder !== "") {
    let sortField = currentStateData.brand_campaign_sorting_measure.sortField;
    let sortOrder = currentStateData.brand_campaign_sorting_measure.sortOrder;
    campaignListApiEndpoint = campaignListApiEndpoint + "&sort_by=" + sortField + "&sort_dir=" + sortOrder;
  } else {
    campaignListApiEndpoint = campaignListApiEndpoint + "&sort_by=campaignName&sort_dir=asc";
  }

  // check if filters are applied, then we need to fetch the filtered list.
  if (typeof search_term !== "undefined" && search_term !== "") {
    // search 
    campaignListApiEndpoint = campaignListApiEndpoint + "&search_term=" + search_term;
  }

  const campaignList = await sendApiRequest(campaignListApiEndpoint, {}, "GET");

  if (typeof campaignList.success !== "undefined" && campaignList.success === true) {

    store.dispatch(
      updateStateData({
        shareable_campaign_list: campaignList.response.content,
      })
    );
    // save/update campaign pagination info
    manageShareableCampaignPaginationCookie({
      totalPages: campaignList.response.totalPages,
      totalRecords: campaignList.response.totalElements,
      pageNumber: campaignList.response.number,
      pageSize: campaignList.response.size,
    });

    return {
      success: true,
    };
  } else {
    return {
      success: false,
    };
  }
};

export const resetUserData = () => {
  store.dispatch(deleteItems([
    "cyreen_user",
    "username",
    "email",
    "roles",
    "user_assigned_company",
    "user_assigned_companies",
    "selected_company_id",
    "selected_company_ids",
    "tenant_hidden_company_ids",
    "workspace_ids",
    "company_selection_mode",
    "campaign_id",
    "user_interacted",
    "brand_campaign_sorting_measure",
    "brand_campaign_selection",
  ]));
}

export const saveBrandInformation = async (actionToPerform, actionDetail, refreshUserType = "brand") => {
  const currentStateData = store.getState().userSelections;
  const email = typeof currentStateData.email !== "undefined" ? currentStateData.email : "";
  const userRole = ["Brand"];
  const campaignIds =
    typeof currentStateData.brand_campaign_selection !== "undefined" && currentStateData.brand_campaign_selection !== null
      ? currentStateData.brand_campaign_selection
      : [];
  const cyreenUser = typeof currentStateData.cyreen_user !== "undefined" ? currentStateData.cyreen_user : false;
  // workspace_id workspace_name
  const userWorkspaceId =
    typeof currentStateData.selected_company_id !== "undefined" ? currentStateData.selected_company_id : "";

  // validate
  let error = false;
  let campaignError = false;
  let errorMessages = {};

  //validate user name
  if (email === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      email: "Please enter the E-Mail",
    };
  } else {
    // validate for proper email format
    const validateEmail = validateEmailFormat(email);
    if (validateEmail === false) {
      error = true;
      errorMessages = {
        ...errorMessages,
        email: "Please enter a valid E-Mail",
      };
    }
  }

  if (userWorkspaceId === "" || userWorkspaceId === "__add_to_new_company__") {
    error = true;
    errorMessages = {
      ...errorMessages,
      user_company: "Please select the company",
    };
  }

  if (campaignIds.length === 0) {
    error = true;
    campaignError = true;
    errorMessages = {
      ...errorMessages,
      campaign_id: "Select at least one shared campaign for this brand account",
    };
  }

  // check for errors
  if (error === false) {
    // userWorkspaceId
    let requestObj = {
      username: email,
      email: email,
      roles: userRole,
      campaign_ids: campaignIds,
      cyreen_user: cyreenUser,
    };
    if (userWorkspaceId !== "") {
      requestObj = {
        ...requestObj,
        workspace_id: userWorkspaceId,
      };
    }
    // add assigned company obj if action = update
    if (actionToPerform === "update") {
      requestObj = {
        ...requestObj,
        // assigned_company: assignedCompanyDetails,
      };
    }
    let apiSuccessResponse = "";
    if (actionToPerform === "add") {
      //save user information
      const saveUserInfo = await sendApiRequest("users/add", requestObj, "POST");
      apiSuccessResponse = saveUserInfo.response;
    }

    if (actionToPerform === "update") {
      let requestBody = {
        ...requestObj,
        id: actionDetail.id,
      };
      //save user information
      const saveUserInfo = await sendApiRequest("users/update", requestBody, "POST");
      apiSuccessResponse = saveUserInfo.response;
    }

    if (apiSuccessResponse.success === true) {
      fetchUsers(refreshUserType);
      return {
        success: true,
      };
    } else {
      const toastMessage =
        typeof apiSuccessResponse.error_detail !== "undefined" && apiSuccessResponse.error_detail !== ""
          ? apiSuccessResponse.error_detail
          : "Sorry, something went wrong.";
      toast(toastMessage, { type: "error" });
      return {
        success: false,
        errorMessages: {},
      };
    }
  } else {
    if (campaignError === true) {
      toast(errorMessages.campaign_id, { type: "error" });
    }
    return {
      success: false,
      errorMessages: errorMessages,
    };
  }
};

const normalizeAccessTypeValue = (accessType) => {
  const normalizedAccessType = typeof accessType !== "undefined" && accessType !== null ? accessType.toString().trim().toLowerCase() : "";
  return normalizedAccessType === "brand access" ? "brand" : normalizedAccessType;
};

export const checkAccessTypeFromCookie = () => {
  if (typeof document.cookie.split("; ").find((row) => row.startsWith("access_types")) !== "undefined") {
    const details = JSON.parse(
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("access_types"))
        .split("=")[1]
    );

    return {
      success: true,
      access_types: details,
    };
  }
  return {
    success: false,
    access_types: {
      has_multiple_access: false,
      access_types: [],
    },
  };
}

// return options for access selection drop down in the side nav (bottom sec)
export const accessOptions = () => {
  const checkAccesstype = checkAccessTypeFromCookie();
  const workspaceOptions = checkWorkSpaceFromCookies();

  let Options = workspaceOptions.success === true && typeof workspaceOptions.workspace_options !== "undefined" && workspaceOptions.workspace_options.length !== 0 ? workspaceOptions.workspace_options.map((workspace) => {
    return { label: workspace.name, value: workspace.id };
  }) : [];
  const accessTypeItems = typeof checkAccesstype.access_types !== "undefined" && typeof checkAccesstype.access_types.access_types !== "undefined" ? checkAccesstype.access_types.access_types : [];
  const hasBrandAccess = accessTypeItems.some((accessTypeItem) => normalizeAccessTypeValue(accessTypeItem.access_type) === "brand");

  if (hasBrandAccess === true) {
    Options = [
      { label: "Brand Access", value: "brandAccess" },
      ...Options
    ]
  }
  return Options;
}

// check if the logged in user can share report with a brand user or mark them as favorite
export const canAssignOrMarkFavorite = (user) => {
  const checkUserDetails = checkUserDetailsFromCookie();
  const assignedCompanyDetails = Array.isArray(user.assigned_companies) ? user.assigned_companies : [];
  const loggedInUserType = checkUserDetails?.user_detail?.user_type ?? "";
  const loggedInCompanyName = checkUserDetails?.user_detail?.assigned_company?.name ?? "";
  const normalizedUserType = typeof loggedInUserType === "string" ? loggedInUserType.toLowerCase() : "";
  const brandUserBelongsToLoggedInCompany = assignedCompanyDetails.some(
    (company) => company.name === loggedInCompanyName
  );
  // Tenant can favorite only brand users of their own company. Cyreen can still favorite any brand user.
  const checkAccess =
    (canEditAcrossCompanies(loggedInUserType) === true && normalizedUserType !== "tenant")
    || brandUserBelongsToLoggedInCompany === true;

  return checkAccess;
}
